import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "you have a hunch, not a ticker" walkthrough. Code-driven, no video.
//
// Built from script 5 in docs/video-scripts.md. Two beats matter here and the
// demo is arranged around them: a question in plain English comes back as real
// tickers, and those tickers go straight into the same scoring the rest of the
// site uses. The closing line about judgment is deliberate, it is the honest
// boundary and it is what separates this from the stock-tip crowd.

const QUESTION = 'semiconductor companies with strong margins that are not the obvious names';

const FOUND = [
  { sym: 'MPWR', why: 'Gross margin 55%, no fabs', conv: 4.1 },
  { sym: 'ONTO', why: 'Process control, 53% margin', conv: 3.7 },
  { sym: 'LRCX', why: 'Etch leader, 47% margin', conv: 3.9 },
  { sym: 'ALAB', why: 'Connectivity, margin expanding', conv: 3.2 },
];

const convColor = (v) => (v >= 3.8 ? '#6495ED' : v >= 3.0 ? '#FBBF24' : '#EF4444');

export default function ResearchDemo({ autoPlay = true, loop = true }) {
  // 0=typing, 1=searching, 2=tickers found, 3=graded
  const [phase, setPhase] = useState(0);
  const [typedQ, setTypedQ] = useState('');
  const [visible, setVisible] = useState(0);
  const [graded, setGraded] = useState(false);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase(0); setTypedQ(''); setVisible(0); setGraded(false);

    let qi = 0;
    const qInt = setInterval(() => {
      qi++;
      setTypedQ(QUESTION.slice(0, qi));
      if (qi >= QUESTION.length) clearInterval(qInt);
    }, 34);
    timers.current.push(qInt);

    const searchStart = QUESTION.length * 34 + 500;
    timers.current.push(setTimeout(() => setPhase(1), searchStart));

    // Results land one at a time, so it reads as a search rather than a list
    // that was sitting there all along.
    timers.current.push(setTimeout(() => setPhase(2), searchStart + 1500));
    FOUND.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisible(i + 1), searchStart + 1700 + i * 360));
    });

    const gradeAt = searchStart + 1700 + FOUND.length * 360 + 900;
    timers.current.push(setTimeout(() => { setPhase(3); setGraded(true); }, gradeAt));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 14000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/research</span>
      </div>
      <div className="demo-body">
        {/* The asked question, pinned once it has been sent. */}
        <div style={{
          background: 'var(--bg-primary, #0C1220)',
          border: '1px solid var(--border-subtle, #1E293B)',
          borderRadius: 10, padding: '0.7rem 0.8rem', marginBottom: '0.75rem',
          fontSize: '0.78rem', lineHeight: 1.5,
          color: phase === 0 ? 'var(--text-secondary, #94A3B8)' : 'var(--text-primary, #E2E8F0)',
          minHeight: 40,
        }}>
          {phase === 0 ? typedQ : QUESTION}
          {phase === 0 && <span className="demo-cursor" />}
        </div>

        {phase === 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.4rem 0' }}>
            <div className="demo-typing-dots"><span /><span /><span /></div>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #94A3B8)' }}>
              Searching for candidates
            </span>
          </div>
        )}

        {phase >= 2 && (
          <>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.5rem',
            }}>
              <span style={{
                fontSize: '0.66rem', color: 'var(--text-muted, #64748B)',
                textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
              }}>
                {FOUND.length} candidates
              </span>
              <span className={`demo-btn ${graded ? 'clicked' : ''}`} style={{ fontSize: '0.66rem' }}>
                {graded ? 'Scored' : 'Grade all'}
              </span>
            </div>

            {FOUND.slice(0, visible).map((f) => (
              <div key={f.sym} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'var(--bg-primary, #0C1220)',
                border: '1px solid var(--border-subtle, #1E293B)',
                borderRadius: 8, padding: '0.5rem 0.65rem', marginBottom: '0.4rem',
                animation: 'slideInUp 0.3s ease both',
              }}>
                <span style={{
                  fontFamily: 'var(--font-mono, monospace)', fontWeight: 800,
                  fontSize: '0.82rem', color: 'var(--neon-green, #00D4AA)',
                  minWidth: 48,
                }}>{f.sym}</span>

                <span style={{
                  flex: 1, fontSize: '0.68rem',
                  color: 'var(--text-secondary, #94A3B8)',
                }}>{f.why}</span>

                {/* The grade only appears after the screen runs, so the two
                    steps stay visually distinct. */}
                <span style={{
                  fontFamily: 'var(--font-mono, monospace)', fontSize: '0.7rem',
                  fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: 4,
                  color: convColor(f.conv),
                  background: graded ? 'rgba(100,149,237,0.12)' : 'transparent',
                  opacity: graded ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                  minWidth: 30, textAlign: 'right',
                }}>
                  {f.conv}
                </span>
              </div>
            ))}
          </>
        )}

        {phase >= 3 && (
          <div style={{
            marginTop: '0.55rem', paddingTop: '0.55rem',
            borderTop: '1px solid var(--border-subtle, #1E293B)',
            fontSize: '0.7rem', color: 'var(--text-secondary, #94A3B8)',
            lineHeight: 1.5, animation: 'fadeIn 0.4s ease both',
          }}>
            A vague idea to a graded list, same 34 tests. It will not tell you what
            to buy. It finds candidates, the judgment is still yours.
          </div>
        )}
      </div>
    </div>
  );
}
